import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = {
    chatIds: Array,
    groupIds: Array,
    allowedUserIds: Array,
    resolvedUserIds: Array,
    viewerLatitude: Number,
    viewerLongitude: Number,
    publicRadiusKm: Number,
    appOwner: Boolean
  }

  connect() {
    this.subscriptions = []
    this.mutedStorageKey = "sos_alert_muted_ids"
    this.resolvedStorageKey = "sos_alert_resolved_ids"
    this.resolvedAlertIds = this.restoreResolvedAlertIds()
    this.audibleAlertIds = new Set()
    this.subscribeChats()
    this.subscribeGroups()
    this.subscribePersonal()
  }

  disconnect() {
    if (!this.subscriptions) return
    this.subscriptions.forEach((sub) => sub.unsubscribe())
    this.subscriptions = []
  }

  subscribeChats() {
    const ids = Array.isArray(this.chatIdsValue) ? this.chatIdsValue : []
    ids.forEach((chatId) => {
      if (!chatId) return
      const sub = consumer.subscriptions.create(
        { channel: "LiveLocationsChannel", chat_id: chatId },
        { received: (data) => this.handleIncoming(data) }
      )
      this.subscriptions.push(sub)
    })
  }

  subscribeGroups() {
    const ids = Array.isArray(this.groupIdsValue) ? this.groupIdsValue : []
    ids.forEach((groupId) => {
      if (!groupId) return
      const sub = consumer.subscriptions.create(
        { channel: "LiveLocationsChannel", group_id: groupId },
        { received: (data) => this.handleIncoming(data) }
      )
      this.subscriptions.push(sub)
    })
  }

  subscribePersonal() {
    const sub = consumer.subscriptions.create(
      { channel: "SosAlertsChannel" },
      { received: (data) => this.handleIncoming(data) }
    )
    this.subscriptions.push(sub)
  }

  handleIncoming(data) {
    const kind = data?.kind || (data?.sos ? "sos" : "live")
    const active = data?.active === false ? false : this.activeSosStatus(data?.status)
    const alertId = this.normalizeId(data?.sos_alert_id)
    const alertAllowed = this.allowedUser(data?.user_id)

    if (kind === "sos_stop" || kind === "stop") {
      if (alertId) this.markResolved(alertId)
      if (!alertAllowed && !this.resolvedVisibleUser(data?.user_id)) {
        this.dispatchSosIncoming({ ...data, kind: "sos_remove" })
        return
      }
      this.dispatchSosIncoming(data)
      return
    }

    if (kind !== "sos") return
    if (alertId && this.isResolvedSosAlert(alertId) && active) return
    const publicVisible = (this.publicSos(data) || this.activeStiPublicSos(data)) && this.withinPublicRadius(data)
    if (!publicVisible && !alertAllowed) return

    const detail = {
      user_id: data.user_id,
      userId: data.user_id,
      name: data.name,
      latitude: data.latitude,
      longitude: data.longitude,
      address: data.address,
      recorded_at: data.recorded_at,
      status: data.status,
      active,
      gps_status: data.gps_status,
      live_active: data.live_active,
      user_type: data.user_type,
      owner_type: data.user_type,
      sos_alert_id: alertId,
      visibility: data.visibility,
      redirectPath: data.redirect_path || data.dashboard_path || "/notifications",
      authorityPath: data.authority_path || "/government_agencies",
      responders: Array.isArray(data.responders) ? data.responders : [],
      kind: "sos",
      sos: true,
      public_view_only: publicVisible && !alertAllowed
    }

    this.dispatchSosIncoming(detail)
    if (!active) return
    if (this.isMutedSosAlert(alertId)) return
    if (!alertAllowed) return
    if (alertId && this.audibleAlertIds.has(String(alertId))) return
    if (alertId) this.audibleAlertIds.add(String(alertId))

    document.dispatchEvent(new CustomEvent("sos:alert", {
      detail: {
        source: "global",
        userId: data.user_id,
        name: data.name,
        sosAlertId: alertId,
        active: true,
        status: data.status,
        redirectPath: detail.redirectPath || "/notifications",
        authorityPath: detail.authorityPath || "/government_agencies"
      }
    }))
  }

  isMutedSosAlert(alertId) {
    if (!alertId) return false

    try {
      const raw = window.sessionStorage.getItem(this.mutedStorageKey)
      const values = raw ? JSON.parse(raw) : []
      return Array.isArray(values) && values.map(String).includes(String(alertId))
    } catch (_e) {
      return false
    }
  }

  markResolved(alertId) {
    if (!alertId) return
    this.resolvedAlertIds.add(String(alertId))
    this.persistResolvedAlertIds()
  }

  isResolvedSosAlert(alertId) {
    if (!alertId) return false
    return this.resolvedAlertIds.has(String(alertId))
  }

  persistResolvedAlertIds() {
    try {
      window.sessionStorage.setItem(this.resolvedStorageKey, JSON.stringify(Array.from(this.resolvedAlertIds)))
    } catch (_e) {
      // Ignore storage errors.
    }
  }

  restoreResolvedAlertIds() {
    try {
      const raw = window.sessionStorage.getItem(this.resolvedStorageKey)
      const values = raw ? JSON.parse(raw) : []
      return new Set(Array.isArray(values) ? values.map((value) => String(value)) : [])
    } catch (_e) {
      return new Set()
    }
  }

  normalizeId(value) {
    if (value == null) return ""
    return String(value).trim()
  }

  allowedUser(userId) {
    if (this.appOwnerValue === true) return true
    if (userId == null) return false
    const allowed = Array.isArray(this.allowedUserIdsValue) ? this.allowedUserIdsValue : []
    if (allowed.length === 0) return false

    return allowed.map(String).includes(String(userId))
  }

  publicSos(data = {}) {
    return String(data?.visibility || "").toLowerCase() === "public_feed"
  }

  activeStiPublicSos(data = {}) {
    const type = String(data?.user_type || data?.owner_type || "")
    const active = data?.active === false ? false : this.activeSosStatus(data?.status)
    return active && ["Student", "Entrepreneur", "Professional", "Vip", "GovernmentStaff", "SchoolStaff"].includes(type)
  }

  activeSosStatus(status) {
    return ["active", "acknowledged", "en_route"].includes(String(status || "active").toLowerCase())
  }

  withinPublicRadius(data = {}) {
    const radiusKm = Number(this.publicRadiusKmValue || 1000)
    const viewerLat = Number(this.viewerLatitudeValue)
    const viewerLng = Number(this.viewerLongitudeValue)
    const alertLat = Number(data?.latitude)
    const alertLng = Number(data?.longitude)
    if (![viewerLat, viewerLng, alertLat, alertLng].every(Number.isFinite)) return false
    if (Math.abs(viewerLat) > 90 || Math.abs(alertLat) > 90) return false
    if (Math.abs(viewerLng) > 180 || Math.abs(alertLng) > 180) return false

    const toRadians = (degrees) => degrees * Math.PI / 180
    const earthRadiusKm = 6371
    const latDelta = toRadians(alertLat - viewerLat)
    const lngDelta = toRadians(alertLng - viewerLng)
    const lat1 = toRadians(viewerLat)
    const lat2 = toRadians(alertLat)
    const haversine = Math.sin(latDelta / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(lngDelta / 2) ** 2
    const distanceKm = 2 * earthRadiusKm * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
    return distanceKm <= radiusKm
  }

  resolvedVisibleUser(userId) {
    if (userId == null) return false
    const allowed = Array.isArray(this.resolvedUserIdsValue) ? this.resolvedUserIdsValue : []
    return allowed.map(String).includes(String(userId))
  }

  dispatchSosIncoming(detail) {
    window.__sharedSosRealtimeEvents ||= []
    window.__sharedSosRealtimeEvents.push({ ...detail })
    window.__sharedSosRealtimeEvents = window.__sharedSosRealtimeEvents.slice(-50)
    document.dispatchEvent(new CustomEvent("sos:incoming", { detail }))
  }
}



